import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  adminMobileActionPrimaryClass,
  adminMobileActionSecondaryClass,
  adminMobileBadgeClass,
  adminMobileCardActionsClass,
  adminMobileCardBodyClass,
  adminMobileCardClass,
  adminMobileCardHeaderClass,
  adminMobileCardMetaClass,
  adminMobileCardTitleClass,
  adminMobileListClass,
  adminMobileRowClass,
  adminMobileRowLabelClass,
  adminMobileRowValueClass,
} from "../admin/adminStyles";
import CopyAuditJourneyButton from "./CopyAuditJourneyButton";
import {
  CARD_ENTRY_STATUS_LABELS,
  formatDevice,
  formatEngagement,
  formatLandingPage,
  formatLocation,
  formatTrafficSource,
  getCardEntryStatus,
  getCustomerDisplayName,
  getSessionCustomerProgress,
  getRouteSummary,
  PROGRESS_FIELD_LABELS,
  type AuditSession,
} from "../../utils/bookingFormAuditSession";
import {
  buildAuditCreateBookingState,
  hasAuditCreateBookingData,
} from "../../utils/mapAuditProgressToBookingForm";
import { formatAmount } from "../../utils/formatMoney";
import { formatUkDateTime } from "../../utils/dateUtils";
import { auditStepLabel } from "../../utils/bookingFormAuditSteps";

const BOOKING_SOURCE_LABELS: Record<string, string> = {
  "southampton-port-taxi": "Southampton Port Taxi",
  "airport-transfers-taxi": "Airport Transfers Taxi",
  "uk-airport-taxi": "UK Airport Taxi",
  "liverpool-dock-taxi": "Liverpool Docks Taxi",
  "tikla-cars": "Tikla Cars",
};

const EVENT_LABELS: Record<string, string> = {
  form_started: "Form opened",
  step_viewed: "Step viewed",
  step_completed: "Step completed",
  field_updated: "Field updated",
  form_snapshot: "Form progress snapshot",
  validation_failed: "Validation failed",
  fare_quote_started: "Fare quote started",
  fare_quote_success: "Fare quote received",
  fare_quote_failed: "Fare quote failed",
  vehicle_selected: "Vehicle selected",
  confirm_started: "Confirm started",
  confirm_success: "Confirm success",
  confirm_failed: "Confirm failed",
  payment_step_entered: "Payment step",
  card_input_started: "Card input started",
  card_input_error: "Card input error",
  card_details_completed: "Card details completed",
  payment_intent_started: "Payment started",
  payment_intent_created: "Payment intent created",
  payment_succeeded: "Payment succeeded",
  payment_failed: "Payment failed",
  booking_created: "Booking created",
  booking_failed: "Booking save failed",
  page_viewed: "Page viewed",
  page_engagement: "Time on page",
  cta_clicked: "CTA clicked",
  nav_clicked: "Nav clicked",
  outbound_clicked: "Outbound link",
  call_clicked: "Call clicked",
  link_clicked: "Link clicked",
  language_popup_shown: "Language popup shown",
  language_selected: "Language selected",
  language_popup_dismissed: "Language popup dismissed",
};

function formatSource(source: string): string {
  return BOOKING_SOURCE_LABELS[source] ?? source;
}

function formatEventType(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType.replace(/_/g, " ");
}

function formatDateTime(value: string): string {
  try {
    return formatUkDateTime(value);
  } catch {
    return value;
  }
}

function metadataPreview(
  metadata: Record<string, unknown> | null,
  eventType?: string,
): string {
  if (!metadata || Object.keys(metadata).length === 0) return "";
  if (eventType === "page_engagement") {
    const parts: string[] = [];
    if (metadata.pagePath) parts.push(`Page: ${String(metadata.pagePath)}`);
    if (metadata.activeSec != null) parts.push(`Active: ${String(metadata.activeSec)}s`);
    if (metadata.scrollDepth != null) parts.push(`Scroll: ${String(metadata.scrollDepth)}%`);
    if (metadata.pageExit != null) parts.push(metadata.pageExit ? "Exit" : "Heartbeat");
    return parts.join(" · ");
  }
  if (eventType === "field_updated" && typeof metadata.field === "string") {
    const label = PROGRESS_FIELD_LABELS[metadata.field] ?? metadata.field;
    if (metadata.cleared) return `${label}: (cleared)`;
    const value =
      typeof metadata.value === "boolean"
        ? metadata.value
          ? "Yes"
          : "No"
        : String(metadata.value ?? "");
    return `${label}: ${value}`;
  }
  if (
    eventType === "language_popup_shown" ||
    eventType === "language_selected" ||
    eventType === "language_popup_dismissed"
  ) {
    const parts: string[] = [];
    if (metadata.fromLocale) parts.push(`From: ${String(metadata.fromLocale)}`);
    if (metadata.toLocale) parts.push(`To: ${String(metadata.toLocale)}`);
    if (metadata.suggestedLocale) parts.push(`Suggested: ${String(metadata.suggestedLocale)}`);
    if (metadata.via) parts.push(`Via: ${String(metadata.via)}`);
    return parts.join(" · ");
  }
  return Object.entries(metadata)
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(" · ");
}

type SessionDetailPanelsProps = {
  session: AuditSession;
  progressPanel: ReactNode;
  vehicleNames?: Record<string, string>;
};

/** Shared expanded detail used by mobile cards. */
export function AuditSessionDetailPanels({
  session,
  progressPanel,
  vehicleNames,
}: SessionDetailPanelsProps) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
        <span className="break-all">Session: {session.sessionId}</span>
        <CopyAuditJourneyButton
          session={session}
          vehicleNames={vehicleNames}
        />
      </div>

      {progressPanel}

      {session.visitor && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="font-medium text-gray-800 dark:text-white/90">
            Visitor context
          </div>
          <div className="mt-2 grid gap-2 text-xs text-gray-600">
            <span>Location: {formatLocation(session.visitor)}</span>
            <span>Device: {formatDevice(session.visitor)}</span>
            <span>IP: {session.visitor.ipAddress ?? "—"}</span>
            <span>Language: {session.visitor.language ?? "—"}</span>
            <span>Timezone: {session.visitor.timezone ?? "—"}</span>
            <span>
              Screen:{" "}
              {session.visitor.screenWidth && session.visitor.screenHeight
                ? `${session.visitor.screenWidth}×${session.visitor.screenHeight}`
                : "—"}
            </span>
            <span>Geo source: {session.visitor.geoSource ?? "—"}</span>
            <span>Traffic: {formatTrafficSource(session.visitor)}</span>
            <span>Referrer: {session.visitor.referrer || "Direct"}</span>
            {session.visitor.utmSource ? (
              <span>UTM source: {session.visitor.utmSource}</span>
            ) : null}
            {session.visitor.utmMedium ? (
              <span>UTM medium: {session.visitor.utmMedium}</span>
            ) : null}
            {session.visitor.utmCampaign ? (
              <span>UTM campaign: {session.visitor.utmCampaign}</span>
            ) : null}
            <span>Engagement: {formatEngagement(session.visitor)}</span>
            <span className="break-all">
              Landing: {session.visitor.landingPage || "—"}
            </span>
          </div>
        </div>
      )}

      <div>
        <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-white/90">
          Event timeline
        </h4>
        <ol className="space-y-2">
          {session.events.map((event) => (
            <li
              key={event.id}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900/40"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {formatEventType(event.eventType)}
                </span>
                {event.stepName && (
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600 dark:bg-white/10 dark:text-gray-300">
                    {auditStepLabel(event.stepName)}
                  </span>
                )}
                <span className="text-[11px] text-gray-500">
                  {formatDateTime(event.createdAt)}
                </span>
              </div>
              {metadataPreview(event.metadata, event.eventType) ? (
                <p className="mt-1 text-[11px] leading-snug text-gray-500 break-words">
                  {metadataPreview(event.metadata, event.eventType)}
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

type Props = {
  sessions: AuditSession[];
  expandedSession: string | null;
  onToggle: (sessionId: string) => void;
  renderProgress: (
    session: AuditSession,
    progress: Record<string, unknown>,
  ) => ReactNode;
  vehicleNames?: Record<string, string>;
};

export default function BookingFormAuditSessionCards({
  sessions,
  expandedSession,
  onToggle,
  renderProgress,
  vehicleNames,
}: Props) {
  return (
    <div className={adminMobileListClass}>
      {sessions.map((session) => {
        const expanded = expandedSession === session.sessionId;
        const progress = getSessionCustomerProgress(session);
        const route = getRouteSummary(progress);
        const customerName = getCustomerDisplayName(progress);
        const email =
          (session.customerEmail ?? String(progress.email ?? "")) || "—";
        const fareValue = formatAmount(progress.fare, 0);
        const fare = fareValue === "N/A" ? "—" : fareValue;
        const phone = progress.mobile ? String(progress.mobile) : null;
        const cardStatus = getCardEntryStatus(session);

        return (
          <article key={session.sessionId} className={adminMobileCardClass}>
            <div className={adminMobileCardHeaderClass}>
              <div className="min-w-0">
                <div className="text-[12px] font-medium text-brand-600 dark:text-brand-400">
                  {formatSource(session.bookingSource)}
                </div>
                <h3 className={adminMobileCardTitleClass}>{customerName}</h3>
                <p className={`${adminMobileCardMetaClass} truncate`} title={email}>
                  {email}
                  {phone ? ` · ${phone}` : ""}
                </p>
              </div>
              <span
                className={`${adminMobileBadgeClass} ${
                  session.completed
                    ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400"
                }`}
              >
                {session.completed ? "Completed" : "Abandoned"}
              </span>
            </div>

            {route !== "—" && (
              <p className="mt-2 text-[13px] leading-snug text-gray-700 dark:text-gray-300">
                {route}
              </p>
            )}

            <div className={adminMobileCardBodyClass}>
              <div className={adminMobileRowClass}>
                <span className={adminMobileRowLabelClass}>Last step</span>
                <span className={adminMobileRowValueClass}>
                  {session.lastStepName
                    ? auditStepLabel(session.lastStepName)
                    : "—"}
                </span>
              </div>
              <div className={adminMobileRowClass}>
                <span className={adminMobileRowLabelClass}>Fare</span>
                <span className={adminMobileRowValueClass}>{fare}</span>
              </div>
              {cardStatus !== "not_reached" && (
                <div className={adminMobileRowClass}>
                  <span className={adminMobileRowLabelClass}>Card details</span>
                  <span
                    className={`min-w-0 text-right font-medium ${
                      cardStatus === "completed"
                        ? "text-green-600 dark:text-green-400"
                        : cardStatus === "error"
                          ? "text-red-600 dark:text-red-400"
                          : "text-amber-600 dark:text-amber-400"
                    }`}
                  >
                    {CARD_ENTRY_STATUS_LABELS[cardStatus]}
                  </span>
                </div>
              )}
              <div className={adminMobileRowClass}>
                <span className={adminMobileRowLabelClass}>Started</span>
                <span className={adminMobileRowValueClass}>
                  {formatDateTime(session.startedAt)}
                </span>
              </div>
              <div className={adminMobileRowClass}>
                <span className={adminMobileRowLabelClass}>Last activity</span>
                <span className={adminMobileRowValueClass}>
                  {formatDateTime(session.lastEventAt)}
                </span>
              </div>
              <div className={adminMobileRowClass}>
                <span className={adminMobileRowLabelClass}>Events</span>
                <span className={adminMobileRowValueClass}>{session.eventCount}</span>
              </div>
              <div className={adminMobileRowClass}>
                <span className={adminMobileRowLabelClass}>Device</span>
                <span className={`${adminMobileRowValueClass} line-clamp-2`}>
                  {formatDevice(session.visitor)}
                </span>
              </div>
              <div className={adminMobileRowClass}>
                <span className={adminMobileRowLabelClass}>Location</span>
                <span className={`${adminMobileRowValueClass} line-clamp-2`}>
                  {formatLocation(session.visitor)}
                </span>
              </div>
              <div className={adminMobileRowClass}>
                <span className={adminMobileRowLabelClass}>Traffic</span>
                <span className={adminMobileRowValueClass}>
                  {formatTrafficSource(session.visitor)}
                </span>
              </div>
              <div className={adminMobileRowClass}>
                <span className={adminMobileRowLabelClass}>Landing</span>
                <span
                  className={`${adminMobileRowValueClass} line-clamp-2`}
                  title={formatLandingPage(session.visitor)}
                >
                  {formatLandingPage(session.visitor)}
                </span>
              </div>
              <div className={adminMobileRowClass}>
                <span className={adminMobileRowLabelClass}>Engagement</span>
                <span className={adminMobileRowValueClass}>
                  {formatEngagement(session.visitor)}
                </span>
              </div>
            </div>

            <div className={adminMobileCardActionsClass}>
              {session.bookingDirectId ? (
                <Link
                  to={`/bookings/${session.bookingDirectId}/view`}
                  className={adminMobileActionSecondaryClass}
                >
                  #{session.bookingId ?? "View"}
                </Link>
              ) : hasAuditCreateBookingData(progress) ? (
                <Link
                  to="/bookings/add"
                  state={buildAuditCreateBookingState(
                    progress,
                    session.bookingSource,
                  )}
                  className={adminMobileActionSecondaryClass}
                >
                  Create booking
                </Link>
              ) : (
                <span
                  className={`${adminMobileActionSecondaryClass} pointer-events-none opacity-50`}
                >
                  No booking
                </span>
              )}
              <button
                type="button"
                onClick={() => onToggle(session.sessionId)}
                className={adminMobileActionPrimaryClass}
              >
                {expanded ? "Hide details" : "Details"}
              </button>
            </div>

            {expanded && (
              <div className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-800">
                <AuditSessionDetailPanels
                  session={session}
                  progressPanel={renderProgress(session, progress)}
                  vehicleNames={vehicleNames}
                />
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
